import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { productUrl } from '../lib/productUrl';
import { useCart } from '../contexts/CartContext';
import SEO from '../components/SEO';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogTrigger } from '../components/ui/dialog';
import { ChevronDown, Plus, Minus, ShoppingCart, ArrowRight, Check } from 'lucide-react';
import FastGripLogo from '../components/FastGripLogo';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

const GripKitPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isEditMode = searchParams.get('edit') === 'true';
  const { addItem, cart, removeItem } = useCart();
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [kitItems, setKitItems] = useState({});
  const [openSections, setOpenSections] = useState({});
  const [added, setAdded] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    const fetchGrip = async () => {
      try {
        const res = await fetch(`${API_BASE}/menu/grip-kit`);
        const data = await res.json();
        setSections(data.sections || []);
      } catch (e) {
        console.error('Failed to fetch grip kit products:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchGrip();
  }, []);

  // In edit mode, pre-fill kit items from existing cart kit
  useEffect(() => {
    if (isEditMode && cart.items) {
      const existingKit = cart.items.find(i => i.is_kit);
      if (existingKit && existingKit.kit_items) {
        const restored = {};
        existingKit.kit_items.forEach(ki => {
          restored[ki.product_id] = {
            product_id: ki.product_id,
            product_name: ki.product_name,
            product_sku: ki.product_sku || '',
            daily_price: ki.daily_price || 0,
            image_url: ki.image_url,
            quantity: ki.quantity || 1
          };
        });
        setKitItems(restored);
      }
    }
  }, [isEditMode, cart.items]);

  const toggleSection = (name) => {
    setOpenSections(prev => ({ ...prev, [name]: !prev[name] }));
  };

  const updateKitQty = useCallback((productId, delta) => {
    setKitItems(prev => {
      const current = prev[productId]?.quantity || 0;
      const newQty = Math.max(0, current + delta);
      if (newQty === 0) {
        const next = { ...prev };
        delete next[productId];
        return next;
      }
      return { ...prev, [productId]: { ...prev[productId], quantity: newQty } };
    });
  }, []);

  const addToKit = (product) => {
    setKitItems(prev => ({
      ...prev,
      [product.id]: {
        product_id: product.id,
        product_name: product.name,
        product_sku: product.sku || '',
        daily_price: product.daily_price || 0,
        image_url: product.image_url,
        quantity: (prev[product.id]?.quantity || 0) + 1
      }
    }));
  };

  const selectedItems = Object.values(kitItems).filter(i => i.quantity > 0);
  const kitSubtotal = selectedItems.reduce((sum, i) => sum + i.daily_price * i.quantity, 0);
  
  // Discount tiers: 5% at 100€, 10% at 200€, 15% at 300€
  const DISCOUNT_TIERS = [
    { threshold: 100, rate: 0.05, label: '5%' },
    { threshold: 200, rate: 0.10, label: '10%' },
    { threshold: 300, rate: 0.15, label: '15%' },
  ];
  const activeTier = [...DISCOUNT_TIERS].reverse().find(t => kitSubtotal >= t.threshold) || null;
  const discountRate = activeTier ? activeTier.rate : 0;
  const discountAmount = kitSubtotal * discountRate;
  const kitTotal = kitSubtotal - discountAmount;
  const hasDiscount = activeTier !== null;

  const handleAddKitToCart = () => {
    if (selectedItems.length === 0) return;
    
    // In edit mode, remove the existing kit from cart first
    if (isEditMode) {
      const existingKit = cart.items.find(i => i.is_kit);
      if (existingKit) {
        removeItem(existingKit.product_id);
      }
    }
    
    const kitCartItem = {
      id: `grip-kit-${Date.now()}`,
      name: 'BIJOUTE ELECTRO PERSONNALISEE',
      sku: 'GRIP-KIT',
      daily_price: kitTotal,
      image_url: null,
      is_kit: true,
      kit_items: selectedItems.map(i => ({
        product_id: i.product_id,
        product_name: i.product_name,
        product_sku: i.product_sku,
        daily_price: i.daily_price,
        quantity: i.quantity,
        image_url: i.image_url
      })),
      kit_subtotal: kitSubtotal,
      kit_discount: discountAmount
    };
    addItem(kitCartItem);
    setAdded(true);
    setTimeout(() => {
      setAdded(false);
      if (isEditMode) navigate('/panier');
    }, 1500);
    if (!isEditMode) setKitItems({});
  };

  if (loading) {
    return (
      <div className="min-h-screen py-8">
        <div className="container mx-auto px-4 md:px-8 lg:px-16">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-200 w-64 mb-6" />
            {[1, 2, 3].map(i => (
              <div key={i} className="h-16 bg-gray-200" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8" data-testid="grip-kit-page">
      <SEO title="FASTGRIP — Composez votre kit Grip" description="Configurez votre kit FASTGRIP sur mesure. Matériel de grip professionnel pour la location. Puzzle Video Paris." />
      <div className="container mx-auto px-4 md:px-8 lg:px-16">
        <p className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground mb-1">
          LUMIERE / GRIP
        </p>
        <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight mb-2 flex items-center gap-2" data-testid="grip-kit-heading">
          <FastGripLogo size="2xl" />
          {isEditMode && <span className="text-sm font-normal text-muted-foreground bg-muted px-2 py-0.5">Modification</span>}
        </h1>
        <p className="text-sm text-muted-foreground mb-8">
          Composez votre kit grip sur mesure. Sélectionnez les éléments et {isEditMode ? 'validez pour mettre à jour le panier' : 'ajoutez le kit à votre panier'}. Prix HT/jour.
        </p>

        <div className="flex gap-6">
          {/* Left: Product sections */}
          <main className="flex-1 space-y-2">
            {sections.length === 0 ? (
              <p className="text-muted-foreground py-8 text-center">Aucun produit grip disponible.</p>
            ) : (
              <>
                {/* All sections rendered as collapsible accordions (ROULANTES GRIP first, per API order) */}
                {sections.map((section) => (
                <div key={section.name} className="border border-border" data-testid={`grip-section-${section.name}`}>
                  <button
                    onClick={() => toggleSection(section.name)}
                    className="flex items-center justify-between w-full p-4 text-left hover:bg-surface/50 transition-colors"
                    data-testid={`grip-toggle-${section.name}`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="font-semibold text-sm uppercase tracking-wide">{section.name}</span>
                      <span className="text-xs text-muted-foreground">({section.products.length} produits)</span>
                    </div>
                    <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${openSections[section.name] ? 'rotate-180' : ''}`} />
                  </button>

                  {openSections[section.name] && (
                    <div className="border-t border-border divide-y divide-border/50">
                      {section.products.map((product) => {
                        const inKit = kitItems[product.id];
                        return (
                          <div key={product.id} className="flex items-center gap-3 p-3 hover:bg-surface/30 transition-colors" data-testid={`grip-product-${product.id}`}>
                            <Dialog>
                              <DialogTrigger asChild>
                                <div className="w-14 h-14 bg-surface flex-shrink-0 overflow-hidden group cursor-zoom-in" data-testid={`grip-image-${product.id}`}>
                                  <img
                                    src={product.image_url || 'https://images.unsplash.com/photo-1516035069371-29a1b244cc32?w=100'}
                                    alt={product.name}
                                    className="w-full h-full object-contain transition-transform duration-300 group-hover:scale-[1.8]"
                                    loading="lazy"
                                  />
                                </div>
                              </DialogTrigger>
                              <DialogContent className="max-w-2xl p-0 bg-white border-none">
                                <div className="flex flex-col">
                                  <img
                                    src={product.image_url || 'https://images.unsplash.com/photo-1516035069371-29a1b244cc32?w=1200'}
                                    alt={product.name}
                                    className="w-full h-auto max-h-[80vh] object-contain bg-white"
                                  />
                                  <div className="p-4 border-t border-border">
                                    <a href={productUrl(product)} className="text-sm font-semibold hover:text-primary hover:underline transition-colors">
                                      {product.name}
                                    </a>
                                    {product.daily_price > 0 && (
                                      <p className="text-xs text-primary font-semibold mt-1">{product.daily_price.toFixed(2)} €/jour</p>
                                    )}
                                  </div>
                                </div>
                              </DialogContent>
                            </Dialog>
                            <div className="flex-1 min-w-0">
                              <a href={productUrl(product)} className="text-sm font-medium line-clamp-1 hover:text-primary hover:underline transition-colors">{product.name}</a>
                              {product.daily_price > 0 && (
                                <p className="text-xs text-primary font-semibold">{product.daily_price.toFixed(2)} €/jour</p>
                              )}
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              {inKit && inKit.quantity > 0 ? (
                                <div className="flex items-center border border-border">
                                  <button
                                    onClick={() => updateKitQty(product.id, -1)}
                                    className="p-2.5 hover:bg-surface transition-colors"
                                    data-testid={`grip-decrease-${product.id}`}
                                  >
                                    <Minus className="w-4 h-4" />
                                  </button>
                                  <span className="px-3 text-sm font-semibold min-w-[32px] text-center">{inKit.quantity}</span>
                                  <button
                                    onClick={() => updateKitQty(product.id, 1)}
                                    className="p-2.5 hover:bg-surface transition-colors"
                                    data-testid={`grip-increase-${product.id}`}
                                  >
                                    <Plus className="w-4 h-4" />
                                  </button>
                                </div>
                              ) : (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="rounded-none h-10 text-base px-4"
                                  onClick={() => addToKit(product)}
                                  data-testid={`grip-add-${product.id}`}
                                >
                                  <Plus className="w-4 h-4 mr-1.5" /> Ajouter
                                </Button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))
              }
              </>
            )}
          </main>

          {/* Right: Sticky kit summary */}
          <aside className="hidden lg:block w-72 flex-shrink-0">
            <Card className="border border-border rounded-none sticky top-24" data-testid="kit-summary">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <ShoppingCart className="w-4 h-4" /> Votre kit
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {selectedItems.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    Aucun élément sélectionné
                  </p>
                ) : (
                  <>
                    <div className="space-y-2 max-h-[50vh] overflow-y-auto">
                      {selectedItems.map((item) => (
                        <div key={item.product_id} className="flex justify-between items-start text-sm border-b border-border/50 pb-2">
                          <div className="min-w-0 flex-1">
                            <p className="font-medium text-xs line-clamp-1">{item.product_name}</p>
                            <p className="text-xs text-muted-foreground">
                              {item.quantity}x {item.daily_price.toFixed(2)} €
                            </p>
                          </div>
                          <span className="text-xs font-semibold text-primary ml-2 flex-shrink-0">
                            {(item.daily_price * item.quantity).toFixed(2)} €
                          </span>
                        </div>
                      ))}
                    </div>

                    <div className="pt-2 border-t border-border space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Sous-total</span>
                        <span>{kitSubtotal.toFixed(2)} €</span>
                      </div>

                      {hasDiscount && (
                        <div className="flex justify-between text-sm text-green-600 font-medium" data-testid="kit-discount">
                          <span>Remise {activeTier.label}</span>
                          <span>-{discountAmount.toFixed(2)} €</span>
                        </div>
                      )}
                      
                      {kitSubtotal > 0 && (
                        <div className="space-y-2" data-testid="kit-discount-progress">
                          {DISCOUNT_TIERS.map((tier, idx) => {
                            const prevReached = idx === 0 || kitSubtotal >= DISCOUNT_TIERS[idx - 1].threshold;
                            if (!prevReached) return null;
                            const reached = kitSubtotal >= tier.threshold;
                            const remaining = Math.max(0, tier.threshold - kitSubtotal);
                            const progress = Math.min(100, (kitSubtotal / tier.threshold) * 100);
                            return (
                              <div key={tier.threshold}>
                                <div className="w-full bg-gray-200 h-1.5 mb-0.5">
                                  <div
                                    className={`h-1.5 transition-all duration-300 ${reached ? 'bg-green-500' : 'bg-primary'}`}
                                    style={{ width: `${progress}%` }}
                                  />
                                </div>
                                <p className="text-[10px] text-muted-foreground">
                                  {reached ? (
                                    <span className="text-green-600 font-semibold">-{tier.label} débloqué</span>
                                  ) : (
                                    <>Encore <strong>{remaining.toFixed(2)} €</strong> pour <strong>-{tier.label}</strong></>
                                  )}
                                </p>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      <div className="flex justify-between font-bold text-sm pt-1 border-t border-border">
                        <span>Total HT/jour</span>
                        <span className="text-primary">{kitTotal.toFixed(2)} €</span>
                      </div>
                    </div>

                    <Button
                      onClick={handleAddKitToCart}
                      className="w-full rounded-none bg-primary hover:bg-primary/90"
                      data-testid="add-kit-to-cart-btn"
                    >
                      {added ? (
                        <><Check className="w-4 h-4 mr-2" /> {isEditMode ? 'Mis à jour !' : 'Ajouté !'}</>
                      ) : (
                        <><ShoppingCart className="w-4 h-4 mr-2" /> {isEditMode ? 'VALIDER LES MODIFICATIONS' : 'AJOUTER AU PANIER'}</>
                      )}
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>
          </aside>
        </div>

        {/* Mobile kit summary - fixed bottom */}
        {selectedItems.length > 0 && (
          <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t-2 border-primary p-4 z-50 shadow-lg" data-testid="kit-summary-mobile">
            <div className="flex items-center justify-between mb-1">
              <span className="font-semibold text-sm">{selectedItems.length} élément(s)</span>
              {hasDiscount ? (
                <div className="text-right">
                  <span className="text-xs line-through text-muted-foreground mr-1">{kitSubtotal.toFixed(2)} €</span>
                  <span className="font-bold text-primary">{kitTotal.toFixed(2)} €/jour</span>
                  <span className="text-[10px] text-green-600 font-medium ml-1">(-{activeTier.label})</span>
                </div>
              ) : (
                <span className="font-bold text-primary">{kitTotal.toFixed(2)} €/jour</span>
              )}
            </div>
            {!hasDiscount && kitSubtotal > 0 && (
              <p className="text-[10px] text-muted-foreground mb-1">Encore {Math.max(0, DISCOUNT_TIERS[0].threshold - kitSubtotal).toFixed(2)} € pour -{DISCOUNT_TIERS[0].label}</p>
            )}
            <Button
              onClick={handleAddKitToCart}
              className="w-full rounded-none bg-primary hover:bg-primary/90"
            >
              {added ? <><Check className="w-4 h-4 mr-2" /> {isEditMode ? 'Mis à jour !' : 'Ajouté !'}</> : <><ShoppingCart className="w-4 h-4 mr-2" /> {isEditMode ? 'VALIDER' : 'AJOUTER AU PANIER'}</>}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default GripKitPage;
